import { useQuery } from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import { config } from "~/config.ts"
import { axiosPost } from "~/lib"
import { queryKeys } from "~/services/query-keys.ts"
import { User } from "~/types"
import { ONE_MINUTE_MILLISECONDS, SESSION_STORAGE_KEYS, SessionStorage } from "~/utils"

const SESSION_ENDPOINT = `${config.authBackendForFrontend}/auth/token`

type SessionPayload = {
  grant_type: string
  code: string
  redirect_uri: string
  client_id: string
  code_verifier: string
}

type SessionResponse = {
  user: User
}

export const setupSession = async (code: string | null): Promise<User> => {
  if (!code) {
    throw new Error("Authorization code is missing")
  }

  // Extract the redirect URI from the current URL
  const [redirectUri] = window.location.href.split("?")
  const codeVerifier = SessionStorage.getItem(SESSION_STORAGE_KEYS.CODE_VERIFIER) || ""
  if (!codeVerifier) {
    throw new Error("Code verifier is missing")
  }

  // Prepare the payload for the setup session request
  const payload = {
    grant_type: "authorization_code",
    code: code!,
    redirect_uri: redirectUri,
    client_id: config.clientId,
    code_verifier: codeVerifier,
  }

  // Make the setup session using the axiosPost helper
  const { data } = await axiosPost<SessionPayload, AxiosResponse<SessionResponse>>({
    url: SESSION_ENDPOINT,
    payload,
    ContentType: "application/json",
  })

  // Return the authenticated user from the response
  return data.user
}

export type UseAuthSessionProps = {
  code: string | null
}

export const useSetupAuthSession = ({ code }: UseAuthSessionProps) => {
  return useQuery({
    queryKey: queryKeys.setupAuthSession({ code }),
    queryFn: () => setupSession(code),
    enabled: !!code,
    staleTime: ONE_MINUTE_MILLISECONDS,
    retry: 1,
  })
}
